import React from 'react';
import { 
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { SearchOffOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Create a custom theme with Poppins font
const poppinsTheme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  palette: {
    primary: {
      main: '#6366F1', // Indigo
      light: '#818CF8',
      dark: '#4F46E5',
    },
    secondary: {
      main: '#F472B6', // Pink
    },
    background: {
      default: '#F9FAFB',
      paper: '#FFFFFF',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation3: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

const EmptyJobState = () => {
  const theme = poppinsTheme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/jobs');
  };

  return ( 
    <ThemeProvider theme={poppinsTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '90vh',
          backgroundColor: theme.palette.background.default,
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: isMobile ? 4 : 5,
              borderRadius: theme.shape.borderRadius,
              width: '100%',
              backgroundColor: theme.palette.background.paper,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '6px',
                background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
              }}
            />
            
            <Stack
              spacing={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                pt: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  borderRadius: '50%',
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <SearchOffOutlined
                  sx={{
                    fontSize: isMobile ? 60 : 72,
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>

              <Box>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
                  No Job Found
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '90%', mx: 'auto', mb: 1 }}>
                  We couldn't locate the job you're looking for. It may have been removed or the link you followed is no longer valid.
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={handleGoBack}
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  Browse All Jobs
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  color="primary"
                  onClick={() => navigate('/')}
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  Return to Home
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};
export default EmptyJobState;